import { Box, Button, Center, Separator, Heading, Icon, Link, Spinner, Text, VStack } from '@chakra-ui/react';
import { ScoSiteNavBar } from '@src/components/sco/nav/ScoSiteNavBar';
import { BreakpointContextProvider } from '@src/components/sco/utilities/BreakpointProvider';
import { PrettyPrintJSON } from '@src/components/sco/utilities/PrettyPrintJSON';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { useWindow } from '@src/hooks/useWindow';
import { wsClient } from '@src/lib/apolloClient';
import { applySentryUser } from '@src/lib/sentryUser';
import { IS_SSR, IS_SSR_ENABLED } from '@src/lib/server-side-rendering';
import tracker from '@src/lib/tracker';
import React, { useEffect, useRef, useState } from 'react';
import { LuRefreshCcw, LuTriangleAlert } from 'react-icons/lu';
let IDENTIFY_CALLED = false;
export const CurrentUserProvider = (props: {
  children: any;
}) => {
  const window = useWindow();
  const [bypassError, setBypassError] = useState(false);
  const {
    currentUser,
    currentUserLoading,
    currentUserError
  } = useCurrentUser();
  const isMounted = useRef(false);
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    wsClient?.terminate();
  }, [currentUser?.userId]);

  // Browser Sentry events and replays carry the user's id (effects never run during SSR).
  useEffect(() => {
    applySentryUser(currentUser);
  }, [currentUser]);
  useEffect(() => {
    if (!IDENTIFY_CALLED && currentUser?.isRegistered) {
      try {
        tracker.identify({
          company: currentUser.company?.name,
          email: currentUser.email,
          firstName: currentUser.firstName,
          lastName: currentUser.lastName,
          userId: currentUser.userId
        });
        IDENTIFY_CALLED = true;
      } catch (err) {
        console.log(err);
      }
    }
  }, [currentUser?.isRegistered]);
  if (IS_SSR_ENABLED) {
    if (!IS_SSR && !currentUser && currentUserError && !bypassError) {
      return <BreakpointContextProvider>
          <ScoSiteNavBar />
          <Center minHeight={'60vh'} padding={8}>
            <VStack gap={4} width={'full'}>
              <Icon asChild fontSize={'3em'} opacity={'0.5'}>
                <LuTriangleAlert />
              </Icon>
              <Heading>Oops, something went wrong.</Heading>
              <Text>We encountered an error while processing your goal. Please try again.</Text>
              <Button marginTop={2} variant={'cta'} onClick={() => window?.location?.reload()}>
                <LuRefreshCcw />
                Retry
              </Button>
              <Link fontSize={'sm'} variant={'underline'} onClick={() => setBypassError(true)}>
                View Anyway
              </Link>
              {process.env.NEXT_PUBLIC_DEPLOYMENT_ENV === 'development' && <PrettyPrintJSON data={{
              currentUser,
              currentUserLoading,
              currentUserError
            }} />}
            </VStack>
          </Center>
        </BreakpointContextProvider>;
    }
    return <>{props.children}</>;
  } else {
    if (currentUser) {
      return <>{props.children}</>;
    }
    if (currentUserLoading) {
      return <>
          {currentUser && props.children}

          {currentUserLoading && <>
              <ScoSiteNavBar />
              <Center padding={'4em'}>
                <Spinner />
              </Center>
            </>}
        </>;
    }
    if (currentUserError) {
      return <>
          <>
            <ScoSiteNavBar />
            <Center padding={'4em'}>
              <VStack gap={'1em'} width={'full'}>
                <Icon asChild fontSize={'3em'} opacity={'0.5'}>
                  <LuTriangleAlert />
                </Icon>
                <VStack gap={'0.5em'} textAlign={'center'} width={'full'}>
                  <Heading>Unable to load account</Heading>
                  <Text>We encountered an error while trying to load your account information.</Text>
                  <Text>
                    {'Please try again or contact'}{' '}
                    <Link href={'mailto:support@startups.com'} variant={'underline'}>
                      support@startups.com
                    </Link>
                  </Text>
                </VStack>
                {process.env.NEXT_PUBLIC_DEPLOYMENT_ENV !== 'production' && <Box bgColor={'blackAlpha.500'} marginTop={'2em'} maxWidth={'800px'} padding={'1em'} width={'full'}>
                    <Text>Debug Information</Text>
                    <Separator marginY={'1em'} />
                    <code>CurrentUserProvider</code>
                    <Separator marginY={'1em'} />
                    <PrettyPrintJSON data={currentUserError} />
                  </Box>}
              </VStack>
            </Center>
          </>
        </>;
    }
  }
};